import React, { createContext, useState, useContext, useEffect } from 'react';
import { useAuth } from './AuthContext';

const ModuleContext = createContext(null);

// Role to module mapping
const ROLE_MODULE_MAP = {
  super_admin: ['retail', 'wholesale'],
  system_admin: ['retail', 'wholesale'],
  retail_admin: ['retail'],
  branch_admin: ['retail'],
  branch_staff: ['retail'],
  wholesale_admin: ['wholesale'],
  wholesale_staff: ['wholesale'],
  admin: ['retail'], // legacy role
  staff: ['retail'] // legacy role
};

// Roles that can choose their module
const MULTI_MODULE_ROLES = ['super_admin', 'system_admin'];

export const ModuleProvider = ({ children }) => {
  const { user, isAuthenticated } = useAuth();
  const [activeModule, setActiveModuleState] = useState(null);
  const [loading, setLoading] = useState(true);

  // Initialize module from localStorage on mount
  useEffect(() => {
    if (isAuthenticated && user) {
      const savedModule = localStorage.getItem('activeModule');
      const allowedModules = getUserModules();

      if (savedModule && allowedModules.includes(savedModule)) {
        setActiveModuleState(savedModule);
      } else if (!canChooseModule()) {
        // Auto-set for single-module users
        setActiveModuleState(allowedModules[0] || 'retail');
      }
    }
    setLoading(false);
  }, [isAuthenticated, user]);

  // Get modules available to current user based on subscription and tenant config
  const getUserModules = () => {
    if (!user) return [];

    // 1. Get modules allowed by subscription (retail, wholesale, accounting)
    const allowedBySubscription = [];
    if (user.modules?.retail) allowedBySubscription.push('retail');
    if (user.modules?.wholesale) allowedBySubscription.push('wholesale');
    if (user.modules?.accounting) allowedBySubscription.push('accounting');

    // 2. Get modules active for this tenant
    const activeForTenant = user.moduleConfig?.activeModules || ['retail'];

    // 3. Intersection: Available modules are those BOTH allowed by subscription AND active for tenant
    const availableModules = allowedBySubscription.filter((m) => activeForTenant.includes(m));

    // Fallback for legacy users or if no modules found
    if (availableModules.length === 0) {
      if (user.role === 'super_admin' || user.role === 'system_admin')
        return ['retail', 'wholesale'];
      return ['retail'];
    }

    return availableModules;
  };

  // Check if user can choose between modules
  const canChooseModule = () => {
    return getUserModules().length > 1;
  };

  // Check if user needs to select a module (for routing)
  const needsModuleSelection = () => {
    return canChooseModule() && !activeModule;
  };

  // Set active module and persist to localStorage
  const setActiveModule = (module) => {
    const allowedModules = getUserModules();
    if (allowedModules.includes(module)) {
      setActiveModuleState(module);
      localStorage.setItem('activeModule', module);
      return true;
    }
    return false;
  };

  // Clear module selection (used on logout)
  const clearModule = () => {
    setActiveModuleState(null);
    localStorage.removeItem('activeModule');
  };

  // Get the default redirect path based on role and module
  const getDefaultRedirect = () => {
    if (needsModuleSelection()) {
      return '/select-module';
    }

    const module = activeModule || getUserModules()[0] || 'retail';
    return `/${module}/dashboard`;
  };

  // Check if current user can access a specific module
  const canAccessModule = (module) => {
    return getUserModules().includes(module);
  };

  const value = {
    activeModule,
    setActiveModule,
    clearModule,
    getUserModules,
    canChooseModule,
    needsModuleSelection,
    getDefaultRedirect,
    canAccessModule,
    loading
  };

  return <ModuleContext.Provider value={value}>{children}</ModuleContext.Provider>;
};

export const useModule = () => {
  const context = useContext(ModuleContext);
  if (!context) {
    throw new Error('useModule must be used within a ModuleProvider');
  }
  return context;
};

export default ModuleContext;
